import { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  Box,
  ScrollArea,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { Zap } from 'react-feather';
import { cohortsApi, type SessionDateUpdate } from '../../../services/api/cohorts';
import type { CohortWithDetails } from '../../../types';
import {
  parseLocalDate,
  parseDateOnly,
  isDateInConstraint,
  findNextValidDate,
  getFrequencyDays,
  toDateValue,
  toISODateString,
  type DateConstraint,
} from '../../../utils/dateHelpers';
import classes from './EditModals.module.css';

interface EditSessionsModalProps {
  opened: boolean;
  onClose: () => void;
  cohort: CohortWithDetails;
  onUpdate: (cohort: CohortWithDetails) => void;
}

interface SessionRow {
  id: string;
  sessionNumber: number;
  date: Date | null;
  title: string;
  groupName?: string;
}

export function EditSessionsModal({
  opened,
  onClose,
  cohort,
  onUpdate,
}: EditSessionsModalProps) {
  const [saving, setSaving] = useState(false);
  const [sessions, setSessions] = useState<SessionRow[]>([]);

  // Reset form when modal opens
  useEffect(() => {
    if (opened) {
      if (cohort.sessions && cohort.sessions.length > 0) {
        setSessions(
          cohort.sessions.map(s => ({
            id: s.id,
            sessionNumber: s.session_number,
            date: parseDateOnly(s.scheduled_start),
            title: s.title,
            groupName: s.group_name,
          }))
        );
      } else {
        // Create empty rows based on session_count
        const count = cohort.session_count || 6;
        setSessions(
          Array.from({ length: count }, (_, i) => ({
            id: `temp-${i + 1}`,
            sessionNumber: i + 1,
            date: null,
            title: `Session ${i + 1}`,
          }))
        );
      }
    }
  }, [opened, cohort]);

  const updateSessionDate = (sessionId: string, date: Date | null) => {
    setSessions(prev =>
      prev.map(s => (s.id === sessionId ? { ...s, date } : s))
    );
  };

  const generateDates = () => {
    // Get the first session date as starting point
    const firstSession = sessions.find(s => s.date);
    if (!firstSession?.date) {
      notifications.show({
        title: 'Set First Session',
        message: 'Please set the first session date before generating',
        color: 'orange',
      });
      return;
    }

    const frequencyDays = getFrequencyDays(cohort.session_frequency ?? null);
    const preferredDays = cohort.preferred_days || [];
    const constraints: DateConstraint[] = cohort.date_constraints?.map(c => ({
      start_date: c.start_date,
      end_date: c.end_date,
    })) || [];

    const newSessions = [...sessions];
    let currentDate = new Date(firstSession.date);

    for (let i = 0; i < newSessions.length; i++) {
      if (i === 0) {
        // First session date is already set
        newSessions[i].date = new Date(currentDate);
      } else {
        // Find next valid date (adds frequency interval internally)
        const validDate = findNextValidDate(currentDate, frequencyDays, preferredDays, constraints);
        newSessions[i].date = validDate;
        currentDate = validDate;
      }
    }

    setSessions(newSessions);
    notifications.show({
      title: 'Dates Generated',
      message: 'Session dates have been generated based on your preferences',
      color: 'green',
    });
  };

  const handleSave = async () => {
    // Filter sessions with dates
    const sessionsWithDates = sessions.filter(s => s.date);

    if (sessionsWithDates.length === 0) {
      notifications.show({
        title: 'No Dates Set',
        message: 'Please set at least one session date',
        color: 'orange',
      });
      return;
    }

    try {
      setSaving(true);
      const data: SessionDateUpdate[] = sessions
        .filter(s => s.date)
        .map(s => ({
          session_number: s.sessionNumber,
          scheduled_date: toISODateString(s.date)!,
        }));

      const updated = await cohortsApi.updateSessions(cohort.id, data);
      onUpdate(updated);
      onClose();
      notifications.show({
        title: 'Success',
        message: 'Session dates updated',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update sessions',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  // Check if a date falls within a constraint
  const isDateConstrained = (date: Date): boolean => {
    if (!cohort.date_constraints) return false;
    return isDateInConstraint(
      date,
      cohort.date_constraints.map(c => ({
        start_date: c.start_date,
        end_date: c.end_date,
      }))
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Edit Session Dates"
      size="lg"
    >
      <Stack gap="md">
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            Set the date for each session. Set the first date and use "Generate" to auto-fill.
          </Text>
          <Button
            variant="light"
            leftSection={<Zap size={16} />}
            onClick={generateDates}
            size="xs"
          >
            Generate Dates
          </Button>
        </Group>

        {cohort.date_constraints && cohort.date_constraints.length > 0 && (
          <Box p="sm" bg="orange.0" style={{ borderRadius: 8 }}>
            <Text size="xs" c="orange.7" fw={500}>
              Date Constraints:
            </Text>
            {cohort.date_constraints.map((c, i) => (
              <Text key={i} size="xs" c="orange.6">
                {parseLocalDate(c.start_date).toLocaleDateString()} - {parseLocalDate(c.end_date).toLocaleDateString()}
                {c.label && ` (${c.label})`}
              </Text>
            ))}
          </Box>
        )}

        <ScrollArea h={400}>
          <Stack gap="sm">
            {sessions.map(session => (
              <Box key={session.id} className={classes.sessionRow}>
                <Text className={classes.sessionNumber}>
                  {session.groupName ? `${session.groupName} - ` : ''}Session {session.sessionNumber}
                </Text>
                <DateInput
                  placeholder="Select date"
                  valueFormat="MM/DD/YYYY"
                  value={session.date}
                  onChange={(value) => updateSessionDate(session.id, toDateValue(value))}
                  clearable
                  className={classes.sessionDateInput}
                  excludeDate={(date) => {
                    // Exclude weekends
                    const d = new Date(date);
                    const day = d.getDay();
                    return day === 0 || day === 6;
                  }}
                  renderDay={(date) => {
                    const d = new Date(date);
                    const isConstrained = isDateConstrained(d);
                    return (
                      <div style={{ color: isConstrained ? 'var(--mantine-color-orange-6)' : undefined }}>
                        {d.getDate()}
                      </div>
                    );
                  }}
                />
              </Box>
            ))}
          </Stack>
        </ScrollArea>

        <Group justify="flex-end" className="modal-footer">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving}>
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default EditSessionsModal;
